import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Transport, Producer } from 'mediasoup-client/lib/types'
import type { Device } from 'mediasoup-client/lib/types'
import type { SfuControl } from './sfu-control'
import { vclog, vcDebugEnabled } from './vc-log'
import { rtpStats, localStatsKey } from '../services/rtp-stats'
import {
  cameraStopsWhenHidden,
  createCameraInterruption,
  type CameraInterruption,
} from '../services/camera-interruption'
import { deviceInUse, deviceManager } from '@/features/devices/services/device-manager'
import { isWeakDevice } from '@/features/devices/services/device-capability'
import { useDevicesStore } from '@/features/devices/store/devices.store'
import { emitBackgroundStatus, setPublishedCameraTrack, VirtualBackgroundProcessor, type BackgroundStatus } from '../services/virtual-background'
import { NoiseCancellation } from '../services/noise-cancellation'
import { denoiserRecovery, type DenoiserHost } from '../services/denoiser-recovery'
import { useMeetingSessionStore } from '../store/meeting-session.store'
import { createProducerPauseSync, setProducerPaused } from '../services/producer-pause-sync'
import { getEncodings } from '../services/encoding-ladder'
import { applyLadderToSender } from '../services/producer-encodings'
import { applyCapWithRetry, type LayerSender } from '../services/spatial-layer'
import { probeCodecCapabilities, codecProbeSnapshot, VIDEO_MIME } from '../services/codec-probe'
import { OPUS_MIME } from '@/features/devices/constants/audio-presets'
import { closeRemoteProducer } from '../services/producer-teardown'
import { config } from '@/config/env'

/**
 * The untrimmed state. A ladder is at most three rungs and the server clamps
 * anything beyond, so "cap at 2" means "every rung this capture produces".
 */
const TOP_SPATIAL_LAYER = 2

/**
 * How long to let trim pushes pile up before acting on the last one.
 *
 * A room in motion — people joining, the grid paging, tiles resizing — moves the
 * aggregate repeatedly within a second or two, and each move used to be its own
 * `setParameters` on the send transport. Acting only on the settled value keeps
 * one write per burst. Short, because an UPWARD move is somebody being
 * under-served right now; the server re-asserts on a much slower interval, so
 * this only has to cover the burst, not guarantee delivery.
 */
const LAYER_APPLY_DEBOUNCE_MS = 250

type UseLocalMediaOptions = {
  sendTransport: Transport | null
  device: Device | null
  roomId: string | null
  peerId: string | null
  /** Control-plane adapter — used to pause/resume the SERVER producer on toggle
   *  (a local producer.pause() never reaches the SFU, so remote peers wouldn't
   *  see the camera-off/mute and would render a black tile). */
  control: SfuControl | null
  simulcastEnabled: boolean
  micOn: boolean
  camOn: boolean
  /**
   * What to composite into the PUBLISHED track, or null for the raw camera.
   * Either an image URL or a blur radius — the two are alternative ways to hide
   * the same room, so one field covers both.
   */
  background?: { url?: string; blurPx?: number } | null
  /**
   * The microphone the user picked, in the lobby or from the control bar.
   *
   * Was ignored entirely: capture asked for `audio: {echoCancellation, ...}`
   * with no deviceId, so the meeting always opened the system default however
   * carefully you chose otherwise. Undefined still means "let the browser
   * decide", which is right before a choice has been made.
   */
  micDeviceId?: string | null
  /** The camera the user picked. Was ignored the same way the microphone was. */
  camDeviceId?: string | null
  /**
   * The SFU node holding this peer's producers.
   *
   * Only used as the fallback channel when closing a producer: the control
   * socket is tried first and answers, this is the direct `DELETE` for when it
   * cannot. Needed here because camera-off now CLOSES the video producer, and
   * a close the server never hears about is replayed to the next joiner.
   */
  sfuNodeUrl?: string | null
  /**
   * Browser audio processing. Capture hardcoded all three to true while
   * Settings offered switches that changed nothing.
   */
  audioProcessing?: { echoCancellation: boolean; noiseSuppression: boolean; autoGainControl: boolean }
  /** Capture resolution, frame rate and top-layer bitrate. */
  videoQuality?: { width: number; height: number; frameRate: number; maxBitrate: number }
  /**
   * Rebuild the producers when this changes.
   *
   * There is exactly one caller — the devices store bumps it when a device goes
   * from REFUSED to granted. Producers are created once, in an effect keyed on
   * the transport, so a capture the browser refused at join leaves nothing to
   * resume: without this, granting the camera mid-meeting would light the
   * button and publish nothing. A first grant does not bump it, so the ordinary
   * join is untouched. Ignored once the join has succeeded — see `joinRetry`.
   */
  mediaRetryToken?: number
  /**
   * RNNoise denoising of the published microphone.
   *
   * Not the same thing as `audioProcessing.noiseSuppression`, and never used
   * together with it — see the note where the constraints are built.
   */
  noiseCancellation?: boolean
  /**
   * Capture format from the selected audio preset. Real getUserMedia
   * constraints, applied to the running track when it changes.
   */
  audioFormat?: { sampleRate: number; channelCount: number; sampleSize: number }
  /**
   * Opus encoder options from the selected audio preset.
   *
   * Read once, when the producer is created — `codecOptions` cannot be changed
   * on a running producer — so a preset change reaches the room on the next
   * one, not immediately.
   */
  opus?: {
    opusStereo: boolean
    opusDtx: boolean
    opusFec: boolean
    opusNack: boolean
    opusPtime: number
    opusMaxPlaybackRate: number
    opusMaxAverageBitrate: number
  }
}

/**
 * Tell the devices store what the browser actually said about a capture.
 *
 * This is the path that PUBLISHES, and it was the one place a refusal went
 * unrecorded: `start` caught everything under "non-fatal" and returned, so the
 * control bar went on showing a lit microphone for a device the browser had
 * just refused. The store is what lowers those controls, and it can only do
 * that if something tells it.
 *
 * A grant is recorded too, and matters as much: it is what closes out a
 * previous refusal so the retry token stops asking for a rebuild.
 */
function recordCaptureVerdict(kind: 'audio' | 'video', error: unknown): void {
  const state =
    error instanceof DOMException && error.name === 'NotAllowedError' ? 'denied' : 'error'
  useDevicesStore.getState().setDevicePermission(kind, state)
}

type UseLocalMediaReturn = {
  localStream: MediaStream | null
  audioProducer: Producer | null
  videoProducer: Producer | null
  ready: boolean
}

export function useLocalMedia({
  sendTransport,
  device,
  roomId,
  peerId,
  control,
  simulcastEnabled,
  micOn,
  camOn,
  background = null,
  micDeviceId = null,
  camDeviceId = null,
  sfuNodeUrl = null,
  audioProcessing = { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  videoQuality = { width: 1280, height: 720, frameRate: 30, maxBitrate: 2_500_000 },
  noiseCancellation = false,
  audioFormat = { sampleRate: 48000, channelCount: 1, sampleSize: 16 },
  mediaRetryToken = 0,
  opus = {
    opusStereo: false,
    opusDtx: false,
    opusFec: true,
    opusNack: true,
    opusPtime: 20,
    opusMaxPlaybackRate: 48000,
    opusMaxAverageBitrate: 40000,
  },
}: UseLocalMediaOptions): UseLocalMediaReturn {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)
  const [audioProducer, setAudioProducer] = useState<Producer | null>(null)
  const [videoProducer, setVideoProducer] = useState<Producer | null>(null)
  const [ready, setReady] = useState(false)

  const audioProducerRef = useRef<Producer | null>(null)
  const videoProducerRef = useRef<Producer | null>(null)
  const localStreamRef = useRef<MediaStream | null>(null)
  /** Raw camera track, kept so the background can be turned back off. */
  const rawVideoTrackRef = useRef<MediaStreamTrack | null>(null)
  const backgroundRef = useRef<VirtualBackgroundProcessor | null>(null)
  // Read inside the produce effect without making the background a dependency:
  // re-running that effect would tear down and rebuild both producers.
  const backgroundRefValue = useRef<{ url?: string; blurPx?: number } | null>(background)
  backgroundRefValue.current = background
  // Same reasoning for the mic: the initial capture reads it, but a change must
  // swap one track — not rebuild both producers and drop everyone's audio.
  const micDeviceIdRef = useRef<string | null>(micDeviceId)
  micDeviceIdRef.current = micDeviceId
  // The switch effect must not re-run just because you muted.
  const micOnRef = useRef(micOn)
  micOnRef.current = micOn
  const camDeviceIdRef = useRef<string | null>(camDeviceId)
  camDeviceIdRef.current = camDeviceId
  const camOnRef = useRef(camOn)
  camOnRef.current = camOn
  /**
   * `mediaRetryToken`, followed only while the join has not succeeded.
   *
   * The token is for a join whose capture was refused and left nothing to
   * resume. A running session has nothing to rebuild — the camera opens and
   * publishes from scratch on every camera-on — yet a camera that failed
   * mid-meeting (held by another browser) and then opened fine bumped the
   * token too. The rebuild that followed dropped the microphone producer for
   * everyone, and raced the very camera-on that caused it: its close-when-off
   * stopped the camera the self tile had just started showing, and the tile
   * stayed black until the camera was toggled again.
   */
  const joinRetryRef = useRef(mediaRetryToken)
  if (!ready) joinRetryRef.current = mediaRetryToken
  const joinRetry = joinRetryRef.current
  /**
   * Everything the publish path reads that used to be an effect dependency.
   *
   * EXPERIMENT (camera close-on-off): publishing the camera is no longer
   * reached only from the produce effect — the camera-on toggle runs the same
   * code. Held in refs so `startVideoProducer` can be `useCallback([])`: a
   * callback that changed identity would re-run the toggle effect on unrelated
   * renders, and that effect now PRODUCES rather than flipping a boolean.
   */
  const sendTransportRef = useRef(sendTransport)
  sendTransportRef.current = sendTransport
  const deviceRef = useRef(device)
  deviceRef.current = device
  const controlRef = useRef(control)
  controlRef.current = control
  const sfuNodeUrlRef = useRef(sfuNodeUrl)
  sfuNodeUrlRef.current = sfuNodeUrl
  const roomIdRef = useRef(roomId)
  roomIdRef.current = roomId
  const simulcastEnabledRef = useRef(simulcastEnabled)
  simulcastEnabledRef.current = simulcastEnabled
  /**
   * Bumped by every start and every teardown.
   *
   * A start checks it after each await and cleans up what it opened instead of
   * publishing it. Opening the camera is now getUserMedia + (maybe) a MediaPipe
   * start + a produce round trip, and the user can turn the camera off again
   * during any of the three — which under the pause model was not possible,
   * because there was nothing to be in flight.
   */
  const videoGenerationRef = useRef(0)
  /**
   * Start and teardown are serialised through one chain.
   *
   * Toggling faster than a start completes would otherwise run the teardown
   * against a producer that does not exist yet, and leave the one that arrives
   * a moment later publishing a camera the user has already turned off.
   */
  const camOpChainRef = useRef<Promise<void>>(Promise.resolve())
  /**
   * Bumped by every teardown of the session's producers. A microphone publish
   * checks it after each await, the way a camera start checks
   * `videoGenerationRef`, and puts down what it opened instead of publishing it
   * onto a transport that has gone.
   */
  const audioGenerationRef = useRef(0)
  /**
   * One microphone operation at a time: the first publish, and every move onto
   * another device.
   *
   * They overlap by design. The manager notifies subscribers from inside
   * `setDevice`, so the subscription fires while the effect that called
   * `setDevice` is still awaiting it — and a first unmute can land in the
   * middle of either. Interleaving two denoiser rebuilds over one producer is
   * how you get a published track nothing is feeding.
   */
  const audioPublishChain = useRef<Promise<void>>(Promise.resolve())
  const audioProcessingRef = useRef(audioProcessing)
  audioProcessingRef.current = audioProcessing
  // Hoisted so the produce path and the mid-call update cannot compute
  // different answers — a ladder built for a device we now consider strong,
  // pushed onto a sender that was created weak, is a silent mismatch.
  const canSimulcastRef = useRef(false)
  /**
   * The server's standing ladder trim — the highest rung any viewer still
   * wants. Held in a ref because BOTH writers of the sender's `active` flags
   * have to agree on it: the trim itself, and every ladder reapply that follows
   * the capture (camera switch, quality change). Starts at the top rung, which
   * is the untrimmed state a fresh producer is published in.
   */
  const maxSpatialLayerRef = useRef(TOP_SPATIAL_LAYER)
  const layerApplyChainRef = useRef<Promise<void>>(Promise.resolve())
  const layerApplyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const videoQualityRef = useRef(videoQuality)
  videoQualityRef.current = videoQuality
  const noiseCancellationRef = useRef(noiseCancellation)
  noiseCancellationRef.current = noiseCancellation
  const audioFormatRef = useRef(audioFormat)
  audioFormatRef.current = audioFormat
  const opusRef = useRef(opus)
  opusRef.current = opus
  /**
   * The denoiser, when one is running — or armed to start on the next gesture
   * (`awaitingGesture`), in which case the raw microphone is on air meanwhile.
   */
  const denoiserRef = useRef<NoiseCancellation | null>(null)
  /** Raw microphone, kept so denoising can be switched back off. */
  const rawAudioTrackRef = useRef<MediaStreamTrack | null>(null)
  /**
   * The hook's half of a denoiser's late changes of mind — the denoised stream
   * arriving after a gesture, or a running context going silent. See
   * `denoiserRecovery`. Built once: everything it touches is a ref.
   *
   * `run` queues on `audioPublishChain`, the same chain as `runAudioOp`: a
   * denoiser's late swap must not interleave with a first publish or a mic
   * switch.
   */
  const recoveryHost = useMemo<DenoiserHost>(
    () => ({
      current: () => denoiserRef.current,
      clear: () => {
        denoiserRef.current = null
      },
      producer: () => audioProducerRef.current,
      raw: () => rawAudioTrackRef.current,
      micOn: () => micOnRef.current,
      published: (track) => {
        setLocalStream((prev) => {
          const video = prev?.getVideoTracks() ?? []
          const updated = new MediaStream([track, ...video])
          localStreamRef.current = updated
          return updated
        })
      },
      run: (op) => {
        const next = audioPublishChain.current.then(op)
        audioPublishChain.current = next.catch(() => {})
        return next
      },
    }),
    [],
  )
  /**
   * Whether this hook currently holds a claim on the shared devices.
   *
   * The raw tracks belong to `deviceManager`, not to us: teardown releases the
   * claim and lets the manager decide whether the hardware stops. Stopping them
   * here would take the camera out from under the lobby if the user is on their
   * way back to it — and would do it while another consumer still held a claim.
   */
  const claimedRef = useRef<{ audio: boolean; video: boolean }>({ audio: false, video: false })

  /**
   * Keeps the SERVER's pause state matching ours, driven by the producers
   * themselves rather than by whoever remembered to send it.
   *
   * Rebuilt with `control`, because a new control adapter is a new socket and
   * the old one's desired states belong to producers that went with it.
   */
  const pauseSyncRef = useRef<ReturnType<typeof createProducerPauseSync> | null>(null)
  useEffect(() => {
    if (!control) return
    const sync = createProducerPauseSync(control)
    pauseSyncRef.current = sync
    // Producers that already exist belong to this sync too. `track()` is
    // normally called where they are created, which is the common case — but a
    // control adapter replaced mid-session would otherwise leave live producers
    // bound to a sync that has been disposed, and their mute state would stop
    // reaching the room with nothing to show for it.
    const ap = audioProducerRef.current
    const vp = videoProducerRef.current
    if (ap && !ap.closed) sync.track(ap, 'audio')
    if (vp && !vp.closed) sync.track(vp, 'video')
    // The consumer side already re-syncs here (see useSfuJoin); producers did
    // not, so a socket blip while muted put you back in the room looking live.
    const offReconnect = control.onReconnect(() => {
      vclog('producer', 'producer pause state re-asserted (reconnect)', {})
      sync.resync()
    })
    return () => {
      offReconnect()
      sync.dispose()
      if (pauseSyncRef.current === sync) pauseSyncRef.current = null
    }
  }, [control])

  /** Serialises everything that opens or closes the camera. */
  const runCamOp = useCallback((op: () => Promise<void>): Promise<void> => {
    const next = camOpChainRef.current.then(op, op)
    camOpChainRef.current = next.catch(() => {})
    return next
  }, [])

  /** Serialises everything that publishes the microphone or moves it to another device. */
  const runAudioOp = useCallback((op: () => Promise<void>): Promise<void> => {
    const next = audioPublishChain.current.then(op)
    // The chain must survive a failed op, so it continues from a promise that
    // cannot reject — while the caller still sees the rejection.
    audioPublishChain.current = next.catch(() => {})
    return next
  }, [])

  /**
   * Tell the SFU a camera producer is gone.
   *
   * `producer.close()` is LOCAL and reaches nobody. Without this the SFU keeps
   * the producer and `readyToConsume` replays it to the next person through
   * the door — the orphaned-screen-share bug, arrived at from a camera toggle.
   * See `services/producer-teardown` for why the control socket is preferred
   * and the HTTP route is kept as the fallback.
   *
   * Its own function because there are TWO ways a camera producer stops being
   * wanted: the ordinary teardown, and a `produce()` that resolves after the
   * camera has already been turned off again. The second one is the easier to
   * forget and the more expensive to get wrong — a producer the client never
   * even recorded, left running on the server.
   */
  const freeServerProducer = useCallback(async (producerId: string, reason: string): Promise<void> => {
    const nodeUrl = sfuNodeUrlRef.current
    const rid = roomIdRef.current
    const outcome = await closeRemoteProducer(producerId, {
      control: controlRef.current,
      http: nodeUrl && rid
        ? (id) => fetch(`${nodeUrl}/rooms/${rid}/producers/${id}`, {
            method: 'DELETE',
            headers: { 'x-api-key': config.sfu.apiKey },
          })
        : null,
    })
    vclog('producer', 'camera off — video producer CLOSED', {
      producerId,
      reason,
      via: outcome.via,
      // The field to go looking for: 'none' means the server still holds a
      // camera producer for a camera that is off, and will hand it to whoever
      // joins next.
      orphaned: outcome.via === 'none' ? outcome.reason : false,
    })
  }, [])

  /**
   * EXPERIMENT — camera off CLOSES the producer and the capture.
   *
   * Torn down in dependency order: the producer first, so the room is told
   * before the pixels stop; then the compositor, whose output track only ever
   * existed for that producer; then the device itself.
   *
   * Nothing here is allowed to throw. This runs on a toggle, on a moderator's
   * force-stop and on the way out of a meeting, and a rejection in the middle
   * would leave the camera open with the producer already gone — the exact
   * state the teardown exists to prevent.
   */
  const teardownVideoProducer = useCallback(async (reason: string): Promise<void> => {
    // Anything still in flight belongs to a camera that is now off.
    videoGenerationRef.current++

    const vp = videoProducerRef.current
    videoProducerRef.current = null
    setVideoProducer(null)

    if (vp) {
      rtpStats.unregister(localStatsKey('video'))
      // Local first: it stops the sender immediately, so nothing is still
      // being encoded while the round trip below is in the air.
      try { vp.close() } catch { /* already closed */ }
      await freeServerProducer(vp.id, reason)
    }

    // The compositor and the canvas track it publishes are OURS, so they are
    // stopped rather than released. `stop()` also gives up this consumer's
    // claim on the shared segmentation run, which is what actually ends the
    // MediaPipe loop — under the pause model it kept segmenting a camera
    // nobody could see.
    backgroundRef.current?.stop()
    backgroundRef.current = null

    setPublishedCameraTrack(null)
    rawVideoTrackRef.current = null

    // Give up our claim, then close the device regardless of who else holds
    // one. `release` alone is not enough: the session retains the camera for
    // the whole meeting (see `VcApp`), so the count would go from two to one
    // and the hardware would stay open with its light on — which is the one
    // thing this experiment is meant to change.
    if (claimedRef.current.video) {
      deviceManager.release('video')
      claimedRef.current.video = false
    }
    deviceManager.stop('video')

    // The self-tile renders `localStream`. Leaving a stopped video track in it
    // shows the last captured frame, frozen, instead of the avatar.
    setLocalStream((prev) => {
      if (!prev) return prev
      const updated = new MediaStream(prev.getAudioTracks())
      localStreamRef.current = updated
      return updated
    })
  }, [freeServerProducer])

  /**
   * EXPERIMENT — camera on OPENS the device and publishes a NEW producer.
   *
   * Two callers. The produce effect at join passes the track it already holds
   * from the combined single-prompt capture; the camera-on toggle passes
   * nothing and opens the device itself, because the last camera-off closed
   * it. Everything after the acquire is the join path verbatim — same codec
   * choice, same ladder, same `stopTracks: false` — so the two cannot drift.
   */
  const startVideoProducer = useCallback(async (preAcquired?: MediaStreamTrack | null): Promise<void> => {
    const sendTransport = sendTransportRef.current
    const device = deviceRef.current
    if (!sendTransport || !device) return
    const existing = videoProducerRef.current
    if (existing && !existing.closed) return

    const generation = ++videoGenerationRef.current
    const overtaken = () => generation !== videoGenerationRef.current

    let acquired = preAcquired ?? null
    if (!acquired) {
      try {
        acquired = await deviceManager.acquire('video')
        claimedRef.current.video = true
        useDevicesStore.getState().setDevicePermission('video', 'granted')
      } catch (err) {
        // Turning the camera on is a real getUserMedia now, so it is a real
        // place to be REFUSED — under the pause model the only prompt was at
        // join — or to find the camera taken by another browser or app.
        recordCaptureVerdict('video', err)
        // The devices store lowers the LOBBY's toggle; the meeting's is
        // `camOn`, and nothing else turns it back off. Left on, the control
        // stayed lit and the self tile drew a black frame for a camera that
        // never opened. Why it failed is the device manager's to say.
        useMeetingSessionStore.getState().setCamOn(false)
        vclog('producer', 'camera on FAILED — the device would not open', {
          error: (err as Error).message,
        })
        return
      }
    }
    if (!acquired) return
    if (overtaken()) {
      // Turned off again while the prompt or the capture was up.
      if (claimedRef.current.video) {
        deviceManager.release('video')
        claimedRef.current.video = false
      }
      deviceManager.stop('video')
      return
    }
    const videoTrack = acquired

    videoTrack.enabled = true
    rawVideoTrackRef.current = videoTrack
    // Lets the background dialog preview against the camera we publish
    // instead of opening a second one.
    setPublishedCameraTrack(videoTrack)

    try {
      // Composite BEFORE the first produce. Applying it afterwards would
      // publish a few frames of the real room — brief, and impossible to
      // take back.
      let trackToPublish = videoTrack
      const initialBackground = backgroundRefValue.current
      // Logged either way. "No logs at all" was itself the symptom last
      // time, and silence cannot distinguish "no background selected" from
      // "the pipeline never ran".
      vclog('producer', 'virtual background for this publish', {
        selected: initialBackground?.url || (initialBackground?.blurPx ? `blur(${initialBackground.blurPx}px)` : '(none)'),
      })
      if (initialBackground) {
        try {
          const processor = new VirtualBackgroundProcessor()
          trackToPublish = await processor.start(videoTrack, initialBackground)
          backgroundRef.current = processor
          vclog('producer', 'virtual background applied', {
            url: initialBackground.url || `blur(${initialBackground.blurPx}px)`,
          })
        } catch (err) {
          // Publish the raw camera rather than nothing: a failed effect
          // should cost the effect, not the call.
          vclog('producer', 'virtual background FAILED, publishing raw camera', {
            error: (err as Error).message,
          })
        }
      }
      // Overtaken while MediaPipe was starting. Nothing is published yet, so
      // the compositor is the only thing to put down before giving up.
      if (overtaken()) {
        backgroundRef.current?.stop()
        backgroundRef.current = null
        return
      }

      const canSimulcast = simulcastEnabledRef.current && !isWeakDevice()
      canSimulcastRef.current = canSimulcast

      // Fire-and-forget: this records what the hardware can do, it does not
      // decide anything. Never awaited — a capability probe must not sit in
      // front of the first published frame.
      void probeCodecCapabilities().catch(() => {})

      // Publish H.264 for the camera, simulcast included.
      //
      // This forced VP8 whenever simulcast was on, because H264 simulcast
      // in browsers is patchy and its failure mode is not worse quality,
      // it is a peer whose video never appears. Changed deliberately on
      // 2026-09-04, eyes open, for what happens downstream.
      //
      // Be precise about why, because the obvious reason is wrong: this is
      // NOT what unblocks GPU decode on the mixer. NVDEC on the T4 decodes
      // VP8 perfectly well (`nvvp8dec` is installed), so a VP8 camera was
      // never the thing keeping decode on the CPU — using `vp8dec` and
      // `avdec_h264` instead of the NVDEC elements is, and that is a mixer
      // change, not this one. Measured there: 55.6% CPU, NVDEC 0%.
      //
      // What H264 actually buys:
      //   - NVENC cannot encode VP8 at all, on any NVIDIA part. Every leg
      //     that must re-encode for an H.323 endpoint is H264 already, so a
      //     H264 source keeps one codec across the whole path.
      //   - It makes a passthrough conceivable on the legs that are a 1:1
      //     forward rather than a composite — screenshare-out above all,
      //     where an H264 source and an H264 endpoint could in principle
      //     skip the transcode entirely.
      //   - It removes a codec from the matrix the gateways negotiate.
      //
      // Screen share already publishes H.264 — it names no codec and takes
      // the router default, which is H264-first — so this only aligns the
      // camera with what content sharing has always done.
      //
      // The VP8 fallback is not optional. A device with no H.264 send
      // capability must still publish something, and it is also the codec
      // that reliably does simulcast, so it stays as the safety net rather
      // than being deleted.
      //
      // Watch for: a peer with no video at all rather than degraded video.
      // That is this change failing, and the probe below is what says
      // whether the device claimed H264 hardware encode beforehand.
      // Constrained Baseline (42e01f) in preference to Main (4d0032), by
      // profile-level-id rather than array position: it is the profile every
      // hardware decoder and every H.323 endpoint takes, and the router lists
      // it first for that reason. Falling back to `h264Caps[0]` rather than
      // nothing keeps a browser that matched only Main on H264 instead of
      // dropping it to VP8.
      const videoCaps = device.rtpCapabilities.codecs?.filter((c) => c.kind === 'video') ?? []
      const h264Caps = videoCaps.filter((c) => c.mimeType === VIDEO_MIME.h264)
      const h264Codec =
        h264Caps.find((c) => c.parameters?.['profile-level-id'] === '42e01f') ?? h264Caps[0]
      const vp8Codec = videoCaps.find((c) => c.mimeType === VIDEO_MIME.vp8)
      const cameraCodec = h264Codec ?? vp8Codec

      // Size the ladder from what the camera actually handed us, not from
      // the preset we asked for. `applyConstraints` uses `ideal`, so a
      // camera is free to return something else and frequently does; a
      // ladder built from the request can end up describing frames that
      // were never captured.
      const captured = trackToPublish.getSettings()
      const encodings = getEncodings(captured.width, captured.height, { simulcast: canSimulcast })
      // Shields the camera's bandwidth share from the screen share's: without
      // this, starting or stopping a screen share — which always renegotiates
      // the shared send transport — lets Chrome's estimator rebalance bitrate
      // across every active encoding on it, and the camera visibly stutters
      // (producer score drops, keyframe retries) right at that moment. The old
      // inmeet client set this on every encoding (RoomClient.js
      // DEFAULT_NETWORK_PRIORITIES) and never had this symptom; this one set
      // it nowhere. Only the first rung: Chrome throws "unimplemented
      // parameter" from addTransceiver when networkPriority is set on more
      // than one simulcast encoding, which is why the old client did the same.
      if (encodings[0]) encodings[0].networkPriority = 'high'
      vclog('producer', 'camera encoding ladder', {
        captured: `${captured.width}x${captured.height}`,
        simulcast: canSimulcast,
        // Both halves on one line: what we chose, and what the hardware
        // would have preferred. That comparison is the whole point of
        // restoring the probe, and the thing to go looking for in logs
        // before anyone changes the codec.
        publishing: h264Codec
          ? 'H264 (preferred)'
          : vp8Codec
            ? 'VP8 (no H264 send capability — fallback)'
            : 'router default (H264 first)',
        // The exact profile, because "H264" alone does not distinguish the
        // one every endpoint decodes from the one only some do.
        h264Profile: h264Codec?.parameters?.['profile-level-id'] ?? 'n/a',
        probeSaysBest: codecProbeSnapshot()?.[0]?.codec ?? 'pending',
        rungs: encodings.map((e) => `${e.maxBitrate / 1000}k/${e.scaleResolutionDownBy}x`).join(' '),
      })

      const vp = await sendTransport.produce({
        track: trackToPublish,
        encodings,
        codec: cameraCodec ? (cameraCodec as never) : undefined,
        appData: { sourceType: 'camera' },
        // We own the camera's lifetime, not mediasoup.
        //
        // NOT `zeroRtpOnPause` here, deliberately — see the microphone.
        //
        // It was on both to begin with, and this is the half that came back
        // off. Stopping the RTP means renegotiating to restart it; cameras
        // get toggled far more often than microphones get unmuted; and this
        // producer shares its send transport with the microphone and the
        // screen share. So every camera toggle was interrupting everyone's
        // audio to save something video barely spends: a disabled video
        // track already encodes almost nothing, where a muted microphone
        // with DTX off does not.
        //
        // Video also has the fragile resume. A restarted stream sends
        // inter-frames first, which is the black-tile problem that needed
        // keyframe retries on the consumer side.
        // `stopTracks` defaults to true, and `replaceTrack()` calls
        // `destroyTrack()` on the OUTGOING track — so switching to a
        // composited track STOPPED the raw camera. That killed the very
        // capture segmentation reads from: the mask went empty, and what we
        // published was the background with nobody in it, for us and for
        // every remote peer. Switching back then handed the producer a
        // track mediasoup had already ended, which is the black self-tile.
        // It only ever worked at join because there the raw track was never
        // the producer's track, so nothing stopped it.
        stopTracks: false,
      })

      // Overtaken between `produce()` being sent and its answer arriving. The
      // teardown that overtook us found no producer in the ref — it did not
      // exist yet — so freeing this one is nobody's job but ours. Closing it
      // locally and stopping there is precisely how a producer gets orphaned
      // on the server.
      if (overtaken()) {
        vp.close()
        await freeServerProducer(vp.id, 'overtaken by camera off')
        return
      }
      videoProducerRef.current = vp
      // A newly published producer carries the full ladder, and the server
      // starts a fresh watcher for it whose trim arrives separately. Leaving
      // a previous producer's trim in the ref would have the next ladder
      // reapply switch rungs off that nothing had asked to be off.
      maxSpatialLayerRef.current = TOP_SPATIAL_LAYER
      pauseSyncRef.current?.track(vp, 'video')
      setVideoProducer(vp)
      rtpStats.register(localStatsKey('video'), {
        kind: 'video',
        direction: 'outbound',
        getStats: () => vp.getStats(),
        rtpParameters: vp.rtpParameters as never,
      })

      // Preview what is actually published. Without this the local tile
      // renders the raw camera and composites separately, which means
      // segmentation runs twice on the same frames — and the two copies can
      // disagree about what the room is seeing.
      //
      // Rebuilt from whatever audio is live rather than from a track captured
      // alongside it: this also runs on a camera-on toggle now, long after the
      // microphone was published and after the teardown stripped the video
      // track out of the stream.
      const preview = new MediaStream([
        ...(localStreamRef.current?.getAudioTracks() ?? []),
        trackToPublish,
      ])
      localStreamRef.current = preview
      setLocalStream(preview)
    } catch (err) {
      // Nothing was published, so nothing must be left holding the camera. A
      // failed start that kept the device open would show the indicator light
      // over a control the user is about to be told is off.
      vclog('producer', 'camera on FAILED — nothing published', {
        error: (err as Error).message,
      })
      backgroundRef.current?.stop()
      backgroundRef.current = null
      setPublishedCameraTrack(null)
      rawVideoTrackRef.current = null
      if (claimedRef.current.video) {
        deviceManager.release('video')
        claimedRef.current.video = false
      }
      deviceManager.stop('video')
    }
  }, [freeServerProducer])

  /**
   * Publish the microphone — on the first unmute, not at join.
   *
   * Joining muted used to publish it anyway, paused. That producer was fanned
   * out as a paused consumer to every other peer in the room and held there for
   * the rest of the meeting, because audio consumers are never closed for being
   * idle (the SFU's `_sweepIdleConsumers` skips them). In the 300-bot test with
   * 40 speakers that was ~78k audio consumers on the SFUs for people who never
   * said a word.
   *
   * The device is still opened and HELD at join, so the first unmute needs no
   * permission prompt and no device warm-up — only this publish. After it, mute
   * is a PAUSE again, never a close (unlike the camera): someone who has spoken
   * once is likely to again, and resuming is a flag where publishing is a round
   * trip and a fan-out to the whole room.
   *
   * Two callers: the join, when you arrive unmuted, and the mic toggle on the
   * first unmute. A rebuilt session (rejoin, `sessionLost`) runs the join again,
   * so someone muted at that moment publishes nothing on the new transport even
   * if they had spoken before.
   *
   * The first unmute is slower than the ones after it: one produce round trip,
   * plus the send transport's ICE + DTLS handshake when nothing else has been
   * published on it yet (camera off). Accepted, knowingly.
   *
   * Always reached through `runAudioOp`.
   */
  const startAudioProducer = useCallback(async (): Promise<void> => {
    const sendTransport = sendTransportRef.current
    const device = deviceRef.current
    if (!sendTransport || sendTransport.closed || !device) return
    const existing = audioProducerRef.current
    if (existing && !existing.closed) return
    // Queued behind another op and muted again since. Nothing is published yet,
    // so nothing has to be.
    if (!micOnRef.current) return
    const raw = rawAudioTrackRef.current
    if (!raw || raw.readyState === 'ended') {
      vclog('producer', 'mic on — NOTHING published, no live microphone is held', {
        track: raw ? 'ended' : 'none',
      })
      return
    }

    const generation = audioGenerationRef.current
    const overtaken = () => generation !== audioGenerationRef.current

    let denoiser: NoiseCancellation | null = null
    try {
      raw.enabled = true
      // Denoise BEFORE the first produce, for the same reason the video
      // background is composited first: publishing raw audio and cleaning
      // it up afterwards means the room hears the room.
      let audioToPublish = raw
      if (noiseCancellationRef.current) {
        const candidate = new NoiseCancellation()
        // The recovery callbacks queue on the chain this op is running on, so
        // a late swap waits until this publish has finished.
        const { onLateReady, onSilenced } = denoiserRecovery(candidate, recoveryHost)
        const denoised = await candidate.process(raw, onLateReady, onSilenced)
        const denoisedTrack = denoised?.getAudioTracks()[0]
        if (denoisedTrack) {
          denoisedTrack.enabled = true
          audioToPublish = denoisedTrack
          denoiser = candidate
        } else if (candidate.awaitingGesture) {
          // The context wants a click first. Publish raw now and KEEP the
          // denoiser: stopping it would disarm the retry that moves the
          // producer onto the denoised track on the next click.
          denoiser = candidate
        } else {
          // process() already said why. Publish the raw microphone rather
          // than nothing — a failed effect costs the effect.
          await candidate.stop()
        }
      }
      // The session ended while the denoiser was starting. Nothing is
      // published yet, so the graph is the only thing to put down.
      if (overtaken()) {
        await denoiser?.stop()
        return
      }
      if (denoiser) denoiserRef.current = denoiser

      // Opus, named rather than assumed.
      //
      // It is already what a browser negotiates — the router lists
      // audio/opus first and mediasoup takes the first matching entry — so
      // this changes nothing today. It changes what can go wrong tomorrow.
      // "First in a list" is exactly the implicit guarantee the camera had
      // before it turned out to be publishing VP8 against an H264-first
      // router, and the other three audio entries (G722, PCMU, PCMA) exist
      // only for H.323 interop. Naming Opus means reordering that list
      // cannot put a browser on a narrowband telephony codec.
      //
      // Undefined rather than a hard failure if it is somehow absent:
      // every browser supports Opus, and falling back to the router's
      // order beats publishing no audio at all.
      const opusCodec = device.rtpCapabilities.codecs?.find(
        (c) => c.mimeType === OPUS_MIME && c.kind === 'audio',
      )
      vclog('producer', 'microphone codec', {
        publishing: opusCodec ? 'Opus (chosen explicitly)' : 'router default (opus first)',
        ptime: opusRef.current?.opusPtime,
        maxAverageBitrate: opusRef.current?.opusMaxAverageBitrate,
        dtx: opusRef.current?.opusDtx,
      })

      const ap = await sendTransport.produce({
        track: audioToPublish,
        codec: opusCodec as never,
        // Same shielding as the camera's ladder, and for the same reason:
        // protect the mic's bitrate share from the reallocation a screen
        // share's start/stop triggers on this shared transport.
        encodings: [{ networkPriority: 'high' }],
        // We own the microphone's lifetime. With the default, replaceTrack
        // would stop the track it replaces — and switching denoising off
        // hands the producer the raw mic that a later switch back would
        // then kill. Same trap as the camera.
        stopTracks: false,
        /**
         * `zeroRtpOnPause` deliberately left at mediasoup-client's default
         * (false) — `pause()` sets `track.enabled = false` and keeps the
         * encoder sending real (silent) RTP rather than stopping uplink
         * entirely. This used to be `true` (ae049219, saving the muted
         * device's uplink/encoder CPU), with the known cost noted at the
         * time as "a clipped first syllable" on resume.
         *
         * Reverted: the actual cost was far bigger than that, and it landed
         * on every OTHER listener, not the muted device. Because `opusDtx`
         * is off by design (97b0936b — DTX garbles live speech), stopping
         * RTP entirely on mute gives every listener's decoder nothing to
         * play for the whole muted span, and it fills the gap with
         * packet-loss concealment instead of silence. Measured against 5
         * bots on a 20s-on/15s-off mic cycle: concealedSamples tracked the
         * mute duty cycle almost exactly (~43% of total samples, stable
         * across a 30-minute run) — audible as the mic-toggle-driven
         * breakup this fixes. A listener-side mitigation (pausing or
         * disabling the Consumer's track on `producerPaused`) was tried
         * first and didn't fully solve it — pausing the Consumer costs a
         * renegotiation on every resume, measured WORSE (~55%), and
         * disabling the track only hides the muted peer's own output, not
         * the edge-of-mute glitch or whatever else the wasted concealment
         * work disturbs. Stopping it at the source is the only fix that
         * removes the cause instead of papering over the symptom.
         *
         * The trade is back to continuous full-rate uplink while muted —
         * exactly what ae049219 removed — on the reasoning that sustained,
         * audible corruption for every listener in every meeting outweighs
         * extra uplink/encoder cost on the one muted device.
         */
        // Opus encoder settings. Without this block mediasoup-client clones
        // the ROUTER's advertised fmtp verbatim into the remote answer
        // (RemoteMediaSection: `clone(codec.parameters)`), so
        // `config/default.js`'s `usedtx: 1` — advertised as a capability —
        // silently became the effective encoder setting for every client.
        // The router comment says each client "opts in/out independently";
        // this is the block that actually makes that true.
        // From the selected audio preset. Still passed as a complete block,
        // always: without one mediasoup-client clones the ROUTER's
        // advertised fmtp verbatim into the remote answer
        // (RemoteMediaSection: `clone(codec.parameters)`), so
        // `config/default.js`'s `usedtx: 1` — advertised as a capability —
        // silently becomes the effective encoder setting for every client.
        //
        // What the values mean, and why the conference preset picks them:
        //  - `opusDtx: false` is THE audio-quality fix. DTX stops sending
        //    during anything the encoder's VAD calls silence, clipping word
        //    onsets and eating soft speech. Every preset keeps it off.
        //  - `opusNack` MUST be present. mediasoup-client STRIPS nack from
        //    Opus whenever codecOptions is given without it, and mediasoup
        //    does advertise nack for Opus — omitting it would remove audio
        //    retransmission that works today.
        //  - `opusMaxAverageBitrate` is a floor against bandwidth
        //    estimation starving Opus to a muffled sub-16 kbps.
        //  - `opusMaxPlaybackRate` is the knob people mean by "16 kHz vs
        //    48 kHz"; the RTP clock is always 48000 and is not negotiable.
        //  - `opusPtime: 20` is the WebRTC norm and what the router's
        //    `minptime: 10` floor was written to let the client choose.
        codecOptions: { ...opusRef.current },
      })

      // The session ended between `produce()` being sent and its answer. The
      // transport it went out on is being torn down with everything on it, so
      // closing locally is enough — exactly what the join did before this was
      // extracted from it.
      if (overtaken()) { ap.close(); return }
      audioProducerRef.current = ap
      // From here the producer reports its OWN pause/resume to the server.
      // Nothing else has to remember to, which is the point.
      pauseSyncRef.current?.track(ap, 'audio')
      // Muted again while the produce was in the air. Kept, and paused: from
      // here on the microphone is the pause model.
      if (!micOnRef.current) {
        audioToPublish.enabled = false
        setProducerPaused(ap, true)
      }
      setAudioProducer(ap)
      // What we are SENDING, which is the half a receiver's stats cannot
      // show you — and the half the simulcast work is meant to change.
      rtpStats.register(localStatsKey('audio'), {
        kind: 'audio',
        direction: 'outbound',
        getStats: () => ap.getStats(),
        rtpParameters: ap.rtpParameters as never,
      })
      vclog('producer', 'microphone published', {
        producerId: ap.id,
        denoised: audioToPublish !== raw,
        pausedOnArrival: !micOnRef.current,
      })
    } catch (err) {
      // Nothing was published. The microphone stays held — it is the
      // session's, not this attempt's — so the next unmute can try again.
      vclog('producer', 'mic on FAILED — nothing published', {
        error: (err as Error).message,
      })
      if (denoiser && denoiserRef.current === denoiser) denoiserRef.current = null
      await denoiser?.stop()
    }
  }, [recoveryHost])

  // Create producers once transports are ready
  useEffect(() => {
    if (!sendTransport || !device || !roomId || !peerId) return

    // A view-only attendee produces nothing, so nothing is opened.
    //
    // This is the path that actually publishes — the preview's camera is a
    // different stream entirely — and without this guard a passive attendee got
    // a microphone-and-camera permission prompt on entering the room and then
    // created both producers. `micOn`/`camOn` being false would not have saved
    // it: those set `track.enabled = false`, which publishes a silent, black
    // track rather than publishing nothing. The signaling server refuses to
    // announce the producer either way, so the tracks would have been captured,
    // encoded and uploaded for no one to receive.
    if (useMeetingSessionStore.getState().viewOnly) return

    let cancelled = false

    async function start() {
      try {

        // The devices this meeting will publish, claimed from the one owner
        // rather than opened here.
        //
        // In the ordinary case they are ALREADY OPEN — the lobby preview
        // claimed them and has not let go yet — so joining does not close and
        // re-open the camera, and the first published frame is not waiting on a
        // device to warm up. The settings are pushed before the claim so a
        // capture that has yet to happen starts at the right preset, and one
        // that is already running is re-shaped by applyConstraints instead of
        // being torn down.
        try {
          await deviceManager.setDevice('audio', micDeviceIdRef.current || null)
          await deviceManager.setDevice('video', camDeviceIdRef.current || null)
        } catch {
          // A remembered device that is no longer plugged in. Whatever is
          // already open is better than refusing to join.
        }
        await deviceManager.setAudioProcessing(
          audioProcessingRef.current,
          noiseCancellationRef.current,
          audioFormatRef.current,
        )
        await deviceManager.setVideoQuality(videoQualityRef.current)
        // The camera only when it is on. Joining with it off used to open it
        // anyway, for the single permission prompt, and close it straight
        // after — a blink of the camera light, and with the camera held by
        // another browser, a join that failed over a device nobody had asked
        // for. Camera-on opens it itself (`startVideoProducer`).
        const wantVideo = camOn
        try {
          await deviceManager.prime({ audio: true, video: wantVideo })
        } catch (err) {
          // One request covered both devices, so a refusal refused both. Record
          // it before rethrowing: the outer catch treats this as non-fatal to
          // the meeting, and without this the user would land in a room with
          // two lit controls and no media behind either.
          //
          // A device that would not START is no verdict on the other one,
          // though: the claims below try each alone and find out which it was.
          if (!deviceInUse(err)) {
            recordCaptureVerdict('audio', err)
            if (wantVideo) recordCaptureVerdict('video', err)
            throw err
          }
        }

        // Settled rather than all-or-nothing, so a claim that succeeded is
        // recorded even when its partner failed. Marking them individually is
        // what lets teardown release exactly what is held: releasing a claim
        // that was never taken would decrement someone else's.
        const [audio, video] = await Promise.allSettled([
          deviceManager.acquire('audio'),
          wantVideo ? deviceManager.acquire('video') : Promise.resolve(null),
        ])
        claimedRef.current = {
          audio: audio.status === 'fulfilled',
          video: wantVideo && video.status === 'fulfilled',
        }
        // Per kind, because these two can genuinely differ — a granted
        // microphone and a camera that is not plugged in.
        const devices = useDevicesStore.getState()
        if (audio.status === 'fulfilled') devices.setDevicePermission('audio', 'granted')
        else recordCaptureVerdict('audio', audio.reason)
        if (wantVideo) {
          if (video.status === 'fulfilled') devices.setDevicePermission('video', 'granted')
          else recordCaptureVerdict('video', video.reason)
        }
        // A microphone we cannot open is still fatal to `start`, exactly as it
        // was when this hook opened the devices itself — the outer catch
        // treats it as non-fatal to the meeting.
        if (audio.status === 'rejected') throw audio.reason
        // A camera we cannot open costs the camera, not the meeting. It used to
        // throw here as well, so a camera held by another browser left the
        // join with no microphone published and `ready` never set — every
        // later camera-on then did nothing at all, under a lit button.
        if (video.status === 'rejected') useMeetingSessionStore.getState().setCamOn(false)
        const audioTrack = audio.value
        const videoTrack = video.status === 'fulfilled' ? video.value : null

        if (cancelled) return

        const stream = new MediaStream(
          [audioTrack, videoTrack].filter((t): t is MediaStreamTrack => !!t),
        )
        localStreamRef.current = stream
        setLocalStream(stream)

        // Microphone — opened and HELD, published only when unmuted. See
        // `startAudioProducer` for why joining muted no longer publishes.
        if (audioTrack) {
          audioTrack.enabled = micOnRef.current
          rawAudioTrackRef.current = audioTrack
          // Denoising, with its late-ready and silence recovery, happens inside
          // `startAudioProducer` now, just before the first produce.
          if (micOnRef.current) {
            await runAudioOp(startAudioProducer)
            if (cancelled) return
          } else {
            vclog('producer', 'joined muted — microphone held, nothing published until the first unmute', {})
          }
        }

        // Video producer — EXPERIMENT (camera close-on-off).
        //
        // Only when the camera is actually ON. Joining with it off now
        // publishes nothing and HOLDS nothing: there is no paused producer
        // waiting to be resumed, so the toggle opens the device and produces
        // from scratch.
        if (videoTrack) {
          await runCamOp(() => startVideoProducer(videoTrack))
        } else if (!wantVideo && !camOnRef.current) {
          // Nothing was opened for it, but the lobby's camera can still be
          // live: turned off a moment before Join, it is inside its retirement
          // grace, and the session's join claim (VcApp) keeps it from ever
          // retiring. Close it rather than leave the light on for a camera the
          // user has turned off.
          //
          // Read live, not from this run's `camOn`: a camera turned ON while
          // the join was in flight is open for the self tile already, and
          // closing it here left that tile black. The camera-on effect
          // publishes it once `ready` lands.
          deviceManager.stop('video')
          vclog('producer', 'joined with the camera off — device closed, nothing published', {})
        }

        if (!cancelled) setReady(true)
      } catch {
        // Permissions denied or device unavailable — non-fatal
      }
    }

    void start()

    return () => {
      cancelled = true
      // Anything still publishing the microphone belongs to this session. A
      // counter, not a DOM node: reading its CURRENT value here is the point.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      audioGenerationRef.current++
      rtpStats.unregister(localStatsKey('audio'))
      rtpStats.unregister(localStatsKey('video'))
      audioProducerRef.current?.close()
      audioProducerRef.current = null
      videoProducerRef.current?.close()
      videoProducerRef.current = null
      backgroundRef.current?.stop()
      backgroundRef.current = null
      void denoiserRef.current?.stop()
      denoiserRef.current = null
      // The DERIVED tracks are ours and have to be stopped: the composited
      // canvas and the denoised graph output exist only for this producer.
      // The raw camera and microphone are the manager's, so they are released
      // rather than stopped — the lobby may be about to claim them back, and on
      // a real leave the manager stops them once the last claim is gone.
      const rawTracks = new Set(
        [rawAudioTrackRef.current, rawVideoTrackRef.current].filter(Boolean),
      )
      localStreamRef.current?.getTracks().forEach((t) => {
        if (!rawTracks.has(t)) t.stop()
      })
      rawAudioTrackRef.current = null
      rawVideoTrackRef.current = null
      setPublishedCameraTrack(null)
      if (claimedRef.current.audio) deviceManager.release('audio')
      if (claimedRef.current.video) deviceManager.release('video')
      claimedRef.current = { audio: false, video: false }
      localStreamRef.current = null
      setLocalStream(null)
      setAudioProducer(null)
      setVideoProducer(null)
      setReady(false)
    }
  }, [sendTransport, device, roomId, peerId, joinRetry]) // eslint-disable-line react-hooks/exhaustive-deps

  // Mic mute/unmute.
  //
  // Nothing published yet (joined muted, never unmuted): the first unmute
  // PUBLISHES — see `startAudioProducer`. Gated on `ready` so it cannot race the
  // join, which publishes for whoever arrives unmuted.
  //
  // Published: toggle track.enabled + pause/resume the producer locally AND on
  // the server (so remote peers see the mute). It is never closed again. Depends
  // on `audioProducer` so it re-applies once the async produce() resolves.
  useEffect(() => {
    const ap = audioProducer ?? audioProducerRef.current
    if (!ap || ap.closed) {
      // The held microphone follows the button, so nothing reading it hears
      // you while the control says muted.
      const raw = rawAudioTrackRef.current
      if (raw) raw.enabled = micOn
      if (micOn && ready) {
        vclog('producer', 'mic on — first unmute, publishing the microphone', {})
        void runAudioOp(startAudioProducer)
      }
      return
    }
    const track = ap.track
    if (track) track.enabled = micOn
    // Local only. The server is told by the producer's observer — see
    // `producer-pause-sync`. Sending it from here as well would double up, and
    // this effect re-runs on every producer swap.
    //
    // Idempotent, and that is not a nicety: with `zeroRtpOnPause` a redundant
    // resume() renegotiates the whole send transport, which is what made audio
    // break up. Logged only when something actually moved.
    if (setProducerPaused(ap, !micOn)) {
      vclog('producer', micOn ? 'mic on (audio producer resumed)' : 'mic off (audio producer paused)', { producerId: ap.id })
    }
  }, [micOn, audioProducer, ready, runAudioOp, startAudioProducer])

  // Microphone changes mid-meeting: swap the track, keep the producer.
  //
  // `replaceTrack` keeps the producer id, its codec and every consumer, so the
  // room hears a new microphone without a renegotiation or a gap. Rebuilding
  // the producer would drop your audio for as long as the round trip takes.
  //
  // Extracted from the effect below because the effect is no longer the only
  // caller. A device that dies mid-call is replaced by the manager itself, and
  // the room only hears the replacement if the producer is moved onto it — by
  // exactly this code, reached from the subscription rather than from a store
  // change. Both routes end here so there is one description of what publishing
  // a microphone means.
  const publishAudioTrack = useCallback(async (track: MediaStreamTrack): Promise<void> => {
    const ap = audioProducerRef.current
    if (!ap || ap.closed) {
      // Nothing published yet — joined muted. Hold the new microphone, so the
      // first unmute publishes the device the user picked rather than the one
      // they left (or an ended one, if the old device died). Only while this
      // session holds a microphone at all: before the join's claim, and after
      // teardown, there is nothing here to move.
      if (!claimedRef.current.audio) return
      if (rawAudioTrackRef.current === track && track.readyState === 'live') return
      track.enabled = micOnRef.current
      rawAudioTrackRef.current = track
      setLocalStream((prev) => {
        const video = prev?.getVideoTracks() ?? []
        const updated = new MediaStream([track, ...video])
        localStreamRef.current = updated
        return updated
      })
      return
    }
    // Already on it, and it is alive. Both routes into this function can fire
    // for the same switch — `setDevice` notifies subscribers before it returns
    // to the caller who asked for it — and rebuilding a denoiser twice costs
    // real audio.
    if (rawAudioTrackRef.current === track && ap.track?.readyState === 'live') return

    // Muted stays muted: the replacement inherits `enabled`, so the window
    // between acquiring and replacing is never live while you are on mute.
    track.enabled = micOnRef.current

    // The new microphone needs its own denoiser: the old graph is reading
    // a device we are about to release.
    //
    // A NEW instance, not the running one. Reusing it made one denoiser answer
    // for two microphones, so its late callbacks could not tell which graph
    // they were about — and `process()` begins with `stop()`, which took the
    // live graph off air before the new one existed. The old one is stopped
    // only after the producer has moved.
    const previous = denoiserRef.current
    let next: NoiseCancellation | null = null
    let toPublish = track
    if (noiseCancellationRef.current) {
      const denoiser = new NoiseCancellation()
      const { onLateReady, onSilenced } = denoiserRecovery(denoiser, recoveryHost)
      const denoised = await denoiser.process(track, onLateReady, onSilenced)
      const denoisedTrack = denoised?.getAudioTracks()[0]
      if (denoisedTrack) {
        denoisedTrack.enabled = micOnRef.current
        toPublish = denoisedTrack
        next = denoiser
      } else if (denoiser.awaitingGesture) {
        // Raw for now; its retry swaps the denoised track in.
        next = denoiser
      } else {
        await denoiser.stop()
      }
    }

    if (ap.closed) {
      await next?.stop()
      return
    }
    try {
      await ap.replaceTrack({ track: toPublish })
    } catch (err) {
      // Still on the old microphone and the old graph. Drop the new one.
      await next?.stop()
      throw err
    }
    rawAudioTrackRef.current = track
    denoiserRef.current = next
    await previous?.stop()

    setLocalStream((prev) => {
      const video = prev?.getVideoTracks() ?? []
      const updated = new MediaStream([toPublish, ...video])
      localStreamRef.current = updated
      return updated
    })
  }, [recoveryHost])

  /**
   * One publish at a time, per kind — on the same chain as the first unmute.
   *
   * Two callers, and they overlap by design: the manager notifies subscribers
   * from inside `setDevice`, so the subscription fires while the effect that
   * called `setDevice` is still awaiting it. Whichever lands first does the
   * work; the second finds the guard above already satisfied and returns.
   */
  const queueAudioPublish = useCallback(
    (track: MediaStreamTrack): Promise<void> => runAudioOp(() => publishAudioTrack(track)),
    [runAudioOp, publishAudioTrack],
  )

  useEffect(() => {
    const ap = audioProducer ?? audioProducerRef.current
    const published = ap && !ap.closed ? ap : null
    // Nothing published and no microphone held: the join has not got that far,
    // and it opens the selected device itself. Joined muted is NOT this case —
    // the microphone is held for the first unmute and must follow a switch.
    if (!micDeviceId || (!published && !claimedRef.current.audio)) return

    // Already live on this device — a store write with the same value, or the
    // first render after capture chose it.
    const current = (published ? published.track : rawAudioTrackRef.current)?.getSettings?.().deviceId
    if (current === micDeviceId) return

    let cancelled = false

    const swap = async () => {
      try {
        // The manager opens the new microphone and retires the old one once
        // this effect has moved the producer across.
        const track = await deviceManager.setDevice('audio', micDeviceId)
        if (!track) throw new Error('no audio track for the selected microphone')
        if (cancelled) return
        await queueAudioPublish(track)
        // The device we ACTUALLY got, not the one we asked for. Logging the
        // request back to ourselves is what made a switch that landed on the
        // built-in microphone read as a success.
        vclog('producer', 'microphone switched', {
          requested: micDeviceId,
          actual: track.getSettings().deviceId,
          label: track.label,
        })
      } catch (err) {
        // Keep the microphone that is working rather than going silent.
        vclog('producer', 'microphone switch FAILED, keeping the current one', {
          deviceId: micDeviceId,
          error: (err as Error).message,
        })
      }
    }

    void swap()

    return () => {
      cancelled = true
    }
  }, [micDeviceId, audioProducer, queueAudioPublish])

  // Audio processing toggled mid-meeting.
  //
  // `applyConstraints` reconfigures the running track, so echo cancellation can
  // go off without dropping the microphone or interrupting anyone. Not every
  // browser or device supports every constraint; a rejection leaves the track
  // exactly as it was, which is why the failure is logged rather than retried
  // by re-acquiring — that would cost audio to change a checkbox.
  //
  // The RAW track, never the published one. These three are capture constraints
  // and only the capture device can honour them. `localStreamRef` holds what we
  // publish, which is DeepFilterNet's MediaStreamDestination output once
  // denoising has rebuilt the stream — a synthetic WebAudio track with no
  // microphone behind it. Constraining that reached nothing and rejected into
  // the log below, so after a mic switch or a noise-cancellation toggle these
  // switches silently stopped working until the next capture.
  // Through the device manager, and re-run when noise cancellation moves, for
  // the same reason capture goes through it: the browser's own suppressor must
  // stay off while RNNoise runs. Applying `audioProcessing` verbatim would switch it
  // back on underneath the denoiser the moment any other switch was touched,
  // and toggling RNNoise off would leave the microphone's own filter disabled
  // while the panel showed it enabled.
  useEffect(() => {
    const track = rawAudioTrackRef.current
    if (!track || track.readyState === 'ended') return
    // Through the manager, so the values it would use for the NEXT capture stay
    // in step with what the running track is doing. Applying them here directly
    // left the two to drift, and a later device switch re-opened the microphone
    // with whatever this hook happened to ask for last.
    void deviceManager
      .setAudioProcessing(audioProcessing, noiseCancellation, audioFormat)
      .then((applied) => {
        vclog(
          'producer',
          applied ? 'audio processing updated' : 'audio processing NOT applied by this device',
          { ...audioProcessing, noiseCancellation },
        )
      })
  }, [audioProcessing, noiseCancellation, audioFormat])

  // Noise cancellation toggled mid-meeting.
  //
  // Rebuilds the audio the producer sends rather than re-opening the device:
  // the raw microphone stays exactly as it is and only what we publish changes,
  // so the room hears the switch without a gap or a permission flicker.
  //
  // The swap goes through the same queue as a mic switch. It used to run
  // beside it, so a toggle during a switch could build two graphs — one on the
  // microphone being retired — leak an AudioContext, and leave the producer on
  // a graph nothing feeds. The build itself stays OUTSIDE the queue, because
  // the first activation fetches ~17 MB and must not hold up a mic switch;
  // hence the re-check inside it: a switch, a replaced denoiser or a second
  // toggle may all have happened while DeepFilterNet loaded.
  useEffect(() => {
    const ap = audioProducer ?? audioProducerRef.current
    const raw = rawAudioTrackRef.current
    if (!ap || ap.closed || !raw || raw.readyState === 'ended') return
    // Already in the requested state.
    if (noiseCancellation === !!denoiserRef.current) return

    let cancelled = false

    const apply = async () => {
      try {
        if (noiseCancellation) {
          const denoiser = new NoiseCancellation()
          const { onLateReady, onSilenced } = denoiserRecovery(denoiser, recoveryHost)
          const denoised = await denoiser.process(raw, onLateReady, onSilenced)
          const track = denoised?.getAudioTracks()[0]
          await recoveryHost.run(async () => {
            const stale =
              cancelled ||
              ap.closed ||
              rawAudioTrackRef.current !== raw ||
              denoiserRef.current !== null
            if (stale) {
              await denoiser.stop()
              return
            }
            if (!track) {
              // Waiting for a click: keep it, its retry swaps itself in. This
              // effect also runs when the producer first lands, which is not
              // behind a gesture.
              if (denoiser.awaitingGesture) denoiserRef.current = denoiser
              else await denoiser.stop()
              return
            }
            track.enabled = micOnRef.current
            denoiserRef.current = denoiser
            await ap.replaceTrack({ track })
            recoveryHost.published(track)
          })
        } else {
          await recoveryHost.run(async () => {
            // Toggled back on before this ran: the denoiser stays.
            if (cancelled) return
            const current = denoiserRef.current
            if (!current) return
            denoiserRef.current = null
            // The mic NOW, which a switch may have replaced since the effect ran.
            const live = rawAudioTrackRef.current
            if (live && live.readyState === 'live' && !ap.closed) {
              live.enabled = micOnRef.current
              await ap.replaceTrack({ track: live })
              recoveryHost.published(live)
            }
            await current.stop()
            vclog('producer', 'noise cancellation off', {})
          })
        }
      } catch (err) {
        vclog('producer', 'noise cancellation toggle FAILED', {
          requested: noiseCancellation,
          error: (err as Error).message,
        })
      }
    }

    void apply()

    return () => {
      cancelled = true
    }
  }, [noiseCancellation, audioProducer, recoveryHost])

  // Camera changes mid-meeting.
  //
  // Trickier than the microphone because the raw camera feeds the background
  // compositor as well as the producer, so three things have to move together:
  // the published track, the registered preview track, and segmentation.
  //
  // Extracted for the same reason as the microphone's: a camera that is
  // unplugged mid-call is replaced by the manager, and the room only sees the
  // replacement if something moves the producer onto it.
  const publishVideoTrack = useCallback(async (track: MediaStreamTrack): Promise<void> => {
    const vp = videoProducerRef.current
    if (!vp || vp.closed) return
    if (rawVideoTrackRef.current === track && vp.track?.readyState === 'live') return

    track.enabled = camOnRef.current

    const background = backgroundRefValue.current
    let trackToPublish = track

    if (background && backgroundRef.current) {
      // Old compositor down BEFORE the new one comes up. They would be
      // segmenting two different cameras, so the runs cannot be shared, and
      // two concurrent MediaPipe instances break each other's masks — the
      // published frame becomes a background with nobody in it. The cost is
      // that the room sees a frozen frame for the moment in between, which
      // is a far better failure than a corrupted one. Never fall back to
      // the raw camera here: that would show your actual room.
      backgroundRef.current.stop()
      backgroundRef.current = null
      const processor = new VirtualBackgroundProcessor()
      trackToPublish = await processor.start(track, background)
      // Torn down while MediaPipe was starting. The processor is recorded
      // anyway so teardown stops it rather than leaving it segmenting a camera
      // nobody is watching.
      backgroundRef.current = processor
      if (vp.closed) return
    }

    rawVideoTrackRef.current = track
    setPublishedCameraTrack(track)
    await vp.replaceTrack({ track: trackToPublish })

    // A different camera has its own native size, and replaceTrack keeps the
    // sender parameters that were built for the old one. Same staleness the
    // preset switch had, reached by a different door.
    {
      const settings = track.getSettings()
      const result = await applyLadderToSender(
        vp.rtpSender,
        getEncodings(settings.width, settings.height, { simulcast: canSimulcastRef.current }),
        // Re-size the rungs without contradicting the server's trim. Passing
        // nothing here switched every rung back on behind the server's back,
        // and neither end could tell they now disagreed.
        maxSpatialLayerRef.current,
      )
      vclog('producer', 'ladder follows new camera', {
        result,
        captured: `${settings.width}x${settings.height}`,
        maxSpatialLayer: maxSpatialLayerRef.current,
      })
    }

    setLocalStream((prev) => {
      const audio = prev?.getAudioTracks() ?? []
      const updated = new MediaStream([...audio, trackToPublish])
      localStreamRef.current = updated
      return updated
    })
  }, [])

  /**
   * The server telling us nobody is watching the big rung.
   *
   * This is the half that actually saves the publisher anything. Choosing a
   * rung per viewer already cut what we SEND to each of them; this cuts what we
   * ENCODE at all, so a room where everyone holds you in a thumbnail stops
   * costing you three encoder instances and the whole ladder's uplink.
   *
   * Downward moves are the saving; upward moves must be obeyed promptly or
   * somebody is being under-served, and the server's grace window is what stops
   * it oscillating.
   *
   * This went through `producer.setMaxSpatialLayer()`, which cannot be trusted:
   * mediasoup-client swallows the underlying rejection and records the value as
   * applied regardless, so a refused renegotiation left the rungs off while both
   * ends believed they were on — and its cached value then made every retry of
   * the same number a no-op. See `services/spatial-layer.ts` for the full
   * autopsy. We now write the ladder ourselves and read the sender back.
   */
  const currentLadder = useCallback(() => {
    const s = rawVideoTrackRef.current?.getSettings()
    return getEncodings(s?.width, s?.height, { simulcast: canSimulcastRef.current })
  }, [])

  const requestMaxSpatialLayer = useCallback((layer: number) => {
    maxSpatialLayerRef.current = layer
    if (layerApplyTimerRef.current) clearTimeout(layerApplyTimerRef.current)
    layerApplyTimerRef.current = setTimeout(() => {
      layerApplyTimerRef.current = null
      const run = async () => {
        const target = maxSpatialLayerRef.current
        const result = await applyCapWithRetry(
          () => {
            const vp = videoProducerRef.current
            return vp && !vp.closed ? (vp.rtpSender as LayerSender | null) : null
          },
          currentLadder,
          target,
          {
            // Superseded by a newer push: stop, the next run owns the truth.
            isStale: () => maxSpatialLayerRef.current !== target,
            onAttempt: ({ attempt, result: r }) => {
              if (r === 'applied' || r === 'unchanged' || r === 'unavailable') return
              vclog('producer', 'ladder trim did not take — retrying', {
                spatialLayer: target, attempt, result: r,
              })
            },
          },
        )
        // 'ignored' is the one worth staring at: setParameters resolved and the
        // sender did not change. That is the failure that used to be invisible.
        vclog('producer', 'ladder trimmed to what viewers want', {
          spatialLayer: target, result,
        })
      }
      layerApplyChainRef.current = layerApplyChainRef.current.then(run, run)
    }, LAYER_APPLY_DEBOUNCE_MS)
  }, [currentLadder])

  useEffect(() => {
    if (!control) return
    return control.on('newProducerLayer', (data: unknown) => {
      const msg = data as { producerId: string; spatialLayer: number }
      const vp = videoProducerRef.current
      if (!vp || vp.closed || vp.id !== msg.producerId) return
      requestMaxSpatialLayer(msg.spatialLayer)
    })
  }, [control, requestMaxSpatialLayer])

  // A pending trim must not outlive the hook.
  useEffect(() => () => {
    if (layerApplyTimerRef.current) clearTimeout(layerApplyTimerRef.current)
  }, [])

  /** Same serialisation as the microphone's, for the same overlap. */
  const videoPublishChain = useRef<Promise<void>>(Promise.resolve())
  const queueVideoPublish = useCallback(
    (track: MediaStreamTrack): Promise<void> => {
      const next = videoPublishChain.current.then(() => publishVideoTrack(track))
      videoPublishChain.current = next.catch(() => {})
      return next
    },
    [publishVideoTrack],
  )

  useEffect(() => {
    const vp = videoProducer ?? videoProducerRef.current
    if (!vp || vp.closed || !camDeviceId) return
    if (rawVideoTrackRef.current?.getSettings?.().deviceId === camDeviceId) return

    let cancelled = false

    const swap = async () => {
      try {
        // Same as the microphone: the manager owns opening the new camera and
        // retiring the old one, and it waits for this effect before it does.
        const track = await deviceManager.setDevice('video', camDeviceId)
        if (!track) throw new Error('no video track for the selected camera')
        if (cancelled) return
        await queueVideoPublish(track)
        vclog('producer', 'camera switched', {
          requested: camDeviceId,
          actual: track.getSettings().deviceId,
          label: track.label,
          background: !!backgroundRefValue.current,
        })
      } catch (err) {
        vclog('producer', 'camera switch FAILED, keeping the current one', {
          deviceId: camDeviceId,
          error: (err as Error).message,
        })
      }
    }

    void swap()

    return () => {
      cancelled = true
    }
  }, [camDeviceId, videoProducer, queueVideoPublish])

  /**
   * Follow the device manager when it changes a device WITHOUT being asked.
   *
   * This is the deliberate half of mid-call recovery. The manager opens a
   * replacement when a device in use dies; until now nothing here listened, so
   * the producer learned about it only if the death also happened to change the
   * store — a `devicechange` event, an `enumerateDevices` that had already
   * dropped the entry, and a replacement whose id differed from the dead one.
   * When any of those did not hold, the manager held a live microphone and the
   * room heard an ended track: silence, with nothing logged and nothing shown.
   *
   * Returning the promise is what makes the manager wait: it retires the old
   * track only once every listener has moved across, so `replaceTrack` never
   * lands on a track that has already ended. A null track is a device going
   * away, and is deliberately ignored — the producer keeps what it has until
   * there is something better to put there, because a black tile you can fix is
   * better than a torn-down producer you cannot.
   */
  useEffect(() => {
    const unsubscribeAudio = deviceManager.subscribe('audio', (track) =>
      track ? queueAudioPublish(track) : undefined,
    )
    const unsubscribeVideo = deviceManager.subscribe('video', (track) =>
      track ? queueVideoPublish(track) : undefined,
    )
    return () => {
      unsubscribeAudio()
      unsubscribeVideo()
    }
  }, [queueAudioPublish, queueVideoPublish])

  // Video quality changed mid-meeting.
  //
  // `applyConstraints` reshapes the running capture without re-opening the
  // camera. The ladder then has to follow it: rungs are sized from the capture,
  // so leaving them alone after a preset switch left us asking for an 80-pixel
  // bottom rung and reserving a megabit for a 320-wide frame.
  //
  // Done on BOTH paths. The virtual-background rebuild used to be the only work
  // here and it returned early when no background was set — which is the common
  // case, so the common case did nothing at all.
  useEffect(() => {
    const raw = rawVideoTrackRef.current
    if (!raw || raw.readyState === 'ended') return

    let cancelled = false

    void deviceManager
      .setVideoQuality(videoQuality)
      .then(async (qualityResult) => {
        if (cancelled) return
        // `result` is an object now, not a boolean — `if (!result)` would be
        // true for every outcome including failure, so read the field.
        if (!qualityResult.applied) {
          vclog('producer', 'video quality NOT applied by this camera', {
            requested: `${qualityResult.requested?.width}x${qualityResult.requested?.height}`,
            actual: `${qualityResult.actual?.width}x${qualityResult.actual?.height}`,
            reason: qualityResult.reason,
            // The camera's own ceiling, which is the only thing that answers
            // "does this device support what I picked".
            cameraMax: qualityResult.capabilities
              ? `${qualityResult.capabilities.maxWidth}x${qualityResult.capabilities.maxHeight}@${qualityResult.capabilities.maxFrameRate}`
              : 'unknown',
          })
          // Still follow the capture with the ladder: the track is live at
          // whatever the camera did give us, and sizing rungs to the request
          // rather than the result is the bug getEncodings exists to avoid.
        } else {
          vclog('producer', 'video quality applied', {
            actual: `${qualityResult.actual?.width}x${qualityResult.actual?.height}@${qualityResult.actual?.frameRate}`,
            cameraMax: qualityResult.capabilities
              ? `${qualityResult.capabilities.maxWidth}x${qualityResult.capabilities.maxHeight}@${qualityResult.capabilities.maxFrameRate}`
              : 'unknown',
          })
        }

        const background = backgroundRefValue.current
        const vp = videoProducerRef.current
        if (!vp || vp.closed) return

        // Follow the capture with the ladder, before any compositor rebuild —
        // the sender is live either way and this is the half that was missing.
        const settings = rawVideoTrackRef.current?.getSettings()
        const result = await applyLadderToSender(
          vp.rtpSender,
          getEncodings(settings?.width, settings?.height, { simulcast: canSimulcastRef.current }),
          maxSpatialLayerRef.current,
        )
        vclog('producer', 'ladder follows capture', {
          result,
          captured: `${settings?.width}x${settings?.height}`,
          maxSpatialLayer: maxSpatialLayerRef.current,
        })

        // The compositor sizes its canvas from the camera at start, so a
        // resolution change needs it rebuilt to take effect. Old one down
        // first: two runs on the same camera would be shared, but two
        // COMPOSITORS would both be drawing and only one is published.
        if (!background || !backgroundRef.current) return
        backgroundRef.current.stop()
        backgroundRef.current = null
        const processor = new VirtualBackgroundProcessor()
        const track = await processor.start(raw, background)
        if (cancelled) {
          processor.stop()
          return
        }
        backgroundRef.current = processor
        await vp.replaceTrack({ track })
        setLocalStream((prev) => {
          const audio = prev?.getAudioTracks() ?? []
          const updated = new MediaStream([...audio, track])
          localStreamRef.current = updated
          return updated
        })
      })
      .catch((err) => {
        vclog('producer', 'video quality change FAILED', {
          ...videoQuality,
          error: (err as Error).message,
        })
      })

    return () => {
      cancelled = true
    }
  }, [videoQuality])

  // The background the last run of the effect below saw, so it can tell a
  // background change (reported) from a producer being (re)created — joining,
  // camera back on — with the same background (not).
  const lastBackgroundRef = useRef(background)

  // Background changes mid-meeting: swap what the live producer sends.
  //
  // `replaceTrack` keeps the producer, its id and its codec — so nobody
  // re-negotiates, no consumer is rebuilt, and remote tiles do not flicker.
  // Tearing the producer down and re-creating it would show everyone a black
  // tile for the duration.
  useEffect(() => {
    const vp = videoProducer ?? videoProducerRef.current
    const rawTrack = rawVideoTrackRef.current
    // A dead camera is called out by name. mediasoup throws a bare
    // InvalidStateError('track ended') on replaceTrack, which says nothing
    // about which track or why — and an ended camera here means something
    // stopped it behind our back, exactly the bug this guard came from.
    if (!vp || vp.closed || !rawTrack || rawTrack.readyState === 'ended') {
      vclog('producer', 'virtual background change SKIPPED', {
        requested: background?.url || (background?.blurPx ? `blur(${background.blurPx}px)` : '(none)'),
        hasProducer: !!vp,
        producerClosed: !!vp?.closed,
        hasRawTrack: !!rawTrack,
        rawTrackState: rawTrack?.readyState ?? '(none)',
      })
      return
    }

    let cancelled = false
    const changed = lastBackgroundRef.current !== background
    lastBackgroundRef.current = background
    // Only a change while publishing reports status; the first publish's
    // background is applied before anyone sees the tile.
    const report = (status: BackgroundStatus) => {
      if (changed && !cancelled) emitBackgroundStatus(status)
    }

    const apply = async () => {
      try {
        if (background) {
          report('applying')
          if (backgroundRef.current) {
            // Same pipeline, different image or blur — no new track, no
            // replaceTrack, so remote tiles do not even blink.
            await backgroundRef.current.setBackground(background)
            report('applied')
            return
          }
          const processor = new VirtualBackgroundProcessor()
          const track = await processor.start(rawTrack, background)
          if (cancelled) {
            processor.stop()
            return
          }
          backgroundRef.current = processor
          await vp.replaceTrack({ track })
          setLocalStream((prev) => {
            const audio = prev?.getAudioTracks() ?? []
            const next = new MediaStream([...audio, track])
            localStreamRef.current = next
            return next
          })
          vclog('producer', 'virtual background on', {
            url: background.url || `blur(${background.blurPx}px)`,
          })
          report('applied')
        } else if (backgroundRef.current) {
          // Back to the raw camera. The raw track was never stopped, precisely
          // so this is a swap rather than a fresh getUserMedia.
          await vp.replaceTrack({ track: rawTrack })
          backgroundRef.current.stop()
          backgroundRef.current = null
          setLocalStream((prev) => {
            const audio = prev?.getAudioTracks() ?? []
            const next = new MediaStream([...audio, rawTrack])
            localStreamRef.current = next
            return next
          })
          vclog('producer', 'virtual background off')
          report('removed')
        }
      } catch (err) {
        vclog('producer', 'virtual background change FAILED', {
          error: (err as Error).message,
        })
        report('failed')
      }
    }

    void apply()
    return () => {
      cancelled = true
    }
  }, [background, videoProducer])

  /**
   * Pause the camera for the room while the phone has taken it away — the app
   * was switched or the screen locked. See `services/camera-interruption`.
   *
   * One watcher per producer: camera-off closes the producer, and the next one
   * starts out uninterrupted. It only ever resumes a pause it made itself.
   */
  const interruptionRef = useRef<CameraInterruption | null>(null)
  useEffect(() => {
    const vp = videoProducer
    if (!vp || vp.closed) return
    let pausedByUs = false
    const watch = createCameraInterruption({
      pauseWhenHidden: cameraStopsWhenHidden(),
      onChange: ({ interrupted, cause }) => {
        if (vp.closed) return
        if (interrupted) {
          if (setProducerPaused(vp, true)) pausedByUs = true
        } else if (pausedByUs) {
          setProducerPaused(vp, false)
          pausedByUs = false
        }
        const raw = rawVideoTrackRef.current
        vclog('producer', interrupted
          ? 'camera INTERRUPTED — paused for the room'
          : 'camera back — resumed for the room', {
          cause,
          producerId: vp.id,
          trackMuted: raw?.muted ?? null,
          trackState: raw?.readyState ?? null,
          visibility: typeof document === 'undefined' ? null : document.visibilityState,
        })
      },
      onStillMuted: () => {
        vclog('producer', 'camera STILL muted after returning to the page — not recovering by itself', {
          producerId: vp.id,
          trackState: rawVideoTrackRef.current?.readyState ?? null,
        })
      },
    })
    watch.setTrack(rawVideoTrackRef.current)
    // The camera can be swapped under the producer — a switch, or the manager
    // reopening one that ended. Watch whichever one is feeding it now.
    const unsubscribe = deviceManager.subscribe('video', (track) => {
      if (track) watch.setTrack(track)
    })
    interruptionRef.current = watch
    return () => {
      unsubscribe()
      watch.dispose()
      if (interruptionRef.current === watch) interruptionRef.current = null
    }
  }, [videoProducer])

  /**
   * Laptop stand-ins for a phone losing its camera, so the room's side can be
   * checked without a device. Debug builds (or `vc:debug`) only.
   *
   *   __vcTest.interruptCamera()       what Safari does: the camera is muted
   *   __vcTest.interruptCamera(false)  and comes back
   *   __vcTest.freezeCamera()          what a phone that could not pause does:
   *                                    frames simply stop — the receivers'
   *                                    frozen-tile fallback should catch it
   *   __vcTest.freezeCamera(false)     frames again
   */
  useEffect(() => {
    if (!vcDebugEnabled || typeof window === 'undefined') return
    const w = window as unknown as { __vcTest?: unknown }
    let frozen: MediaStreamTrack | null = null
    w.__vcTest = {
      interruptCamera: (on = true) => {
        const watch = interruptionRef.current
        if (!watch) return 'no camera producer — turn the camera on first'
        watch.simulate(on)
        return on ? 'camera interrupted' : 'camera back'
      },
      freezeCamera: async (on = true) => {
        const vp = videoProducerRef.current
        if (!vp || vp.closed) return 'no camera producer — turn the camera on first'
        if (on && !frozen) {
          frozen = vp.track
          await vp.replaceTrack({ track: null })
        } else if (!on && frozen) {
          const track = frozen
          frozen = null
          await vp.replaceTrack({ track })
        }
        return on ? 'camera frozen' : 'camera flowing'
      },
    }
    return () => {
      delete w.__vcTest
    }
  }, [])

  // Camera on/off — EXPERIMENT: CLOSE the producer and the capture, rather
  // than pausing them.
  //
  // The microphone above shares only the first half: it publishes nothing
  // until the first unmute, but after that it is the pause model and is never
  // closed. The two are deliberately different. Read one against the other.
  //
  // What this gives up, all of it knowingly, so the results are read with eyes
  // open:
  //
  //  - **Turning the camera back on is a round trip, not a flag.** A fresh
  //    getUserMedia, a MediaPipe start if a background is set, a `produce`, and
  //    a `newConsumer` fan-out to every peer in the room before anyone sees a
  //    frame. Pausing was a local boolean and a socket message.
  //  - **A new producer id every time.** Remote consumers are closed and
  //    rebuilt rather than kept, and a fresh stream leads with inter-frames —
  //    the black-tile failure `zeroRtpOnPause: false` and the consumer-side
  //    keyframe retries were both written to avoid. This is where to look
  //    first if remote tiles come back black.
  //  - **A new permission surface.** Camera-on can now be REFUSED mid-meeting.
  //  - **The send transport renegotiates on both edges**, and it carries the
  //    microphone and the screen share too. Watch for audio breaking up on
  //    camera toggles — that is the same mechanism that made `zeroRtpOnPause`
  //    unacceptable for video.
  //
  // What it buys: the camera hardware is genuinely closed and its indicator
  // light goes out, the MediaPipe segmentation loop actually stops, and the
  // SFU holds no producer for a camera that is off.
  //
  // Gated on `ready` so it cannot race the produce effect that owns the join:
  // at that point the producer either exists (joined with the camera on) or is
  // deliberately absent, and either way this agrees with it rather than
  // publishing a second one.
  useEffect(() => {
    if (!ready) return
    void runCamOp(async () => {
      const vp = videoProducerRef.current
      const live = !!vp && !vp.closed
      if (camOn === live) return
      if (camOn) await startVideoProducer()
      else await teardownVideoProducer('camera toggled off')
    })
  }, [camOn, ready, runCamOp, startVideoProducer, teardownVideoProducer])

  return { localStream, audioProducer, videoProducer, ready }
}
